import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";

import Spinner from "@qoohoo/common/library/loader/Spinner";

import useAppState from "store/appState";
import useEntityStore from "store/entityStore";

import { bootUpData } from "helpers/bootup";
import getMailTo from "helpers/getMailTo";
import { checkShouldSEOIndex, getEntitySEOConfig } from "helpers/SEO";

import Navbar from "components/Navbar";
import Footer from "components/Footer";

import FullCheckoutContent from "views/FullCheckoutView/FullCheckoutContent";
import MiniCheckoutContent from "views/MiniCheckoutView/MiniCheckoutContent";

export const ViewLoader = ({
  title,
  description,
  subDescription,
  hideLoader = false,
}) => (
  <div className="h-full w-full flex flex-col justify-center items-center px-5">
    {!hideLoader && <Spinner color="#16191b" className="h-10 w-10" />}

    <span
      className="font-normal text-base mt-6"
      dangerouslySetInnerHTML={{ __html: title }}
    ></span>

    <span
      className="text-[#808587] text-sm font-normal mt-8"
      dangerouslySetInnerHTML={{ __html: description }}
    ></span>
    {subDescription && (
      <span
        className="text-xs font-normal mt-8"
        dangerouslySetInnerHTML={{ __html: subDescription }}
      ></span>
    )}
  </div>
);

function getEmailTag() {
  return `<a style='color:#384AF1;' href='${getMailTo({
    query: "Report an issue with payment",
  })}'>support@qoohoo.in</a>`;
}

function Index({ entityId, entityType, view, date, time, redirect_url }) {
  const setCanCustomerCloseCheckout = useAppState(
    (state) => state.setCanCustomerCloseCheckout
  );
  const { entity, isLoading: isEntityLoading } = useEntityStore(
    (state) => state
  );

  const [isMiniCheckout, setIsMiniCheckout] = useState(null);
  const [isComponentReady, setIsComponentReady] = useState(false);

  useEffect(() => {
    setCanCustomerCloseCheckout(false);
  }, []);

  useEffect(() => {
    // Note: We take book-slot in url, but for all purposes one_to_one_meeting is used as entityType
    if (entityType == "book-slot") {
      entityType = "one_to_one_meeting";
    }

    const options = {
      start_at: time,
      booking_date: date,
      redirect_url: redirect_url,
      is_standalone: true,
    };

    const _isMiniCheckout = view == "mini";
    setIsMiniCheckout(_isMiniCheckout);

    setIsComponentReady(true);

    bootUpData(entityType, entityId, options);
  }, []);

  if (!isComponentReady) return;

  let content;

  if (isMiniCheckout) {
    let contentBody;

    if (isEntityLoading) {
      contentBody = (
        <ViewLoader
          title="Loading details..."
          description="Please do not close this window"
        />
      );
    } else {
      if (entity?.data) {
        contentBody = <MiniCheckoutContent />;
      } else {
        contentBody = (
          <ViewLoader
            title="Something went wrong!"
            description={`Please contact us at ${getEmailTag()}`}
            hideLoader
          />
        );
      }
    }

    content = (
      <div id="container" className="mfix drishy bg-[#F9FAFA]">
        <div id="modal" className="mchild border border-[#EBEBEB]">
          {contentBody}
        </div>
      </div>
    );
  } else {
    let contentBody;
    const seoConfig = getEntitySEOConfig();

    if (isEntityLoading) {
      contentBody = (
        <div className="h-96">
          <ViewLoader
            title="Loading details..."
            description="Please do not close this window"
          />
        </div>
      );
    } else {
      if (entity?.data) {
        contentBody = <FullCheckoutContent isStandalone />;
      } else {
        contentBody = (
          <div className="h-96">
            <ViewLoader
              title="Something went wrong!"
              description={`Please contact us at ${getEmailTag()}`}
              hideLoader
            />
          </div>
        );
      }
    }

    content = (
      <>
        {seoConfig && (
          <NextSeo
            title={seoConfig.title}
            description={seoConfig.description}
            nofollow={!checkShouldSEOIndex()}
            noindex={!checkShouldSEOIndex()}
            openGraph={{
              title: seoConfig.title,
              url: seoConfig.url,
              description: seoConfig.description,
              images: [seoConfig.image],
            }}
          />
        )}
        <div className="min-h-screen w-full flex relative bg-main">
          <div className="w-full flex flex-col">
            <div className="mx-auto w-[90%] max-w-[1044px]">
              <Navbar className="!p-0" />
            </div>
            <div className="fullViewModal !overflow-visible md:my-8 drishy standalone border border-[#EBEBEB] md:rounded-[0.25rem]">
              {contentBody}
            </div>
            <div className="hidden md:block mx-auto w-[90%] max-w-[1044px] mt-auto">
              <Footer className="h-[3.75rem]" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return content;
}

export default Index;

export async function getServerSideProps({ params, query }) {
  const { id: entityId, entityType } = params;
  const { view, date, time, redirect_url } = query;

  return {
    props: {
      entityId,
      entityType,
      view: view || null,
      date: date || null,
      time: time || null,
      redirect_url: redirect_url || null,
    },
  };
}
